/**
 * agent.ts — the FQI policy wrapper for Gold Digger.
 *
 * One policy, learned in-browser. The agent owns:
 *   - a LinearQ (the action-value function; see qnet.ts)
 *   - a Replay buffer (transitions collected during the live demo)
 *   - the FQI round counter (how many times Q has been refit)
 *
 * The main loop calls `agent.act(env)` once per tick; act() computes the
 * 32 candidate features, picks one via masked ε-greedy, steps the env, and
 * appends the transition to the replay buffer. When env.done fires (episode
 * boundary), the main loop calls `agent.fitRound()` to refit Q on the whole
 * buffer — one step of Fitted Q-Iteration. So learning advances round by
 * round, and the user sees the agent improve across episode batches.
 *
 * **Action masking** is baked into selection: a candidate whose belief says
 * the path is blocked (pathClear == 0) is never picked. This is the
 * constraint the env enforces anyway — masking it stops the Q-function
 * from having to rediscover "don't ram walls" from sparse -0.3 reward
 * signals. With masking, the Q-weights learn the INTERESTING trade-off
 * (which direction has more believed gold + unknown area) instead of the
 * trivial one (don't bump).
 *
 * Why FQI + linear Q (not online TD + GRU):
 *   - Online TD on a non-linear approximator is the deadly triad. The old
 *     GRU agent's eval got WORSE with training (39 → 7 gold), Q-values
 *     exploded to 1e51, target networks and clipping didn't save it.
 *   - FQI's frozen-target batch regression cannot diverge. Linear ridge
 *     regression is the exact minimiser of a convex problem. On the 4-world
 *     × 3-seed validation this beats random 1.31–1.54× every which way.
 *
 * Reference: the validated Python prototype (12/12 worlds beat random 1.27–1.47×).
 */

import { Env, N_ACTIONS } from './environment';
import { LinearQ } from './qnet';
import { Replay } from './replay';
import { candidateFeatures, N_FEATURES } from './features';
import { fitRound, epsilonForRound, type FitRoundResult } from './fqi';
import { makeRng } from './linalg';

/** Index of the pathClear feature in the candidate-feature row (features.ts). */
const PATH_CLEAR_FEAT = 3;

export interface AgentOptions {
  seed?: number;
  /** Replay buffer capacity. 100k = ~250 episodes of experience. */
  replayCapacity?: number;
  /** Ridge λ (L2). Default from DEFAULT_LAMBDA. */
  lambda?: number;
  /** Discount γ. Default from DEFAULT_GAMMA. */
  gamma?: number;
}

export const DEFAULT_AGENT_OPTIONS = {
  seed: 0,
  replayCapacity: 100_000,
};

export interface StepResult {
  action: number;
  reward: number;
  done: boolean;
  event: 'gold' | 'bump' | 'new' | 'step';
  /** Q-values for all 32 candidates at the state we acted from. The HUD
   *  shows these as 4 rows × 8 directions. */
  qValues: Float64Array;
  /** ε in effect for this step (the current round's ε). */
  epsilon: number;
}

export interface RoundStats extends FitRoundResult {
  /** Cumulative reward in the round just completed. */
  roundReturn: number;
  /** Gold eaten in the round just completed. */
  roundGold: number;
  /** Episodes in the round just completed. */
  roundEpisodes: number;
}

/**
 * Pick an action via masked ε-greedy.
 *
 * Masking: if `mask[i]` is false, candidate `i` is set to -∞ before argmax.
 * If ALL candidates are masked, the mask is ignored (fallback to plain
 * argmax — sometimes the agent is genuinely surrounded by believed stones
 * and must pick the least-bad option).
 */
export function selectAction(
  q: Float64Array,
  mask: Uint8Array,
  epsilon: number,
  rng: () => number,
): number {
  if (rng() < epsilon) {
    // ε-random: prefer valid (unmasked) candidates when available so random
    // exploration doesn't waste itself on walls.
    let anyValid = false;
    for (let i = 0; i < N_ACTIONS; i++) {
      if (mask[i]) { anyValid = true; break; }
    }
    if (anyValid) {
      // Rejection sample: try a few random indices, take the first valid.
      for (let tries = 0; tries < 8; tries++) {
        const a = Math.floor(rng() * N_ACTIONS);
        if (mask[a]) return a;
      }
      // Fall through to deterministic walk.
      for (let a = 0; a < N_ACTIONS; a++) if (mask[a]) return a;
    }
    return Math.floor(rng() * N_ACTIONS);
  }
  // Greedy: argmax over unmasked candidates.
  let anyValid = false;
  for (let i = 0; i < N_ACTIONS; i++) {
    if (mask[i]) { anyValid = true; break; }
  }
  if (!anyValid) {
    // Fully surrounded — fall back to plain argmax.
    let bi = 0;
    let bv = q[0];
    for (let k = 1; k < N_ACTIONS; k++) {
      if (q[k] > bv) { bv = q[k]; bi = k; }
    }
    return bi;
  }
  let bi = -1;
  let bv = -Infinity;
  for (let k = 0; k < N_ACTIONS; k++) {
    if (!mask[k]) continue;
    if (q[k] > bv) { bv = q[k]; bi = k; }
  }
  return bi;
}

export class Agent {
  readonly q: LinearQ;
  readonly replay: Replay;
  /** Round counter — how many times Q has been refit. Drives the ε schedule. */
  roundCount = 0;
  /** ε for the CURRENT collection round (used by act()). */
  epsilon = 1.0;
  /** The most recent fit-round result (for the HUD). */
  lastFit: RoundStats | null = null;

  // ── Counters (driven by main.ts) ─────────────────────────────────────
  /** Total steps across all episodes this run. */
  totalSteps = 0;
  /** Cumulative reward this run (the total return). */
  returnG = 0;
  /** Per-episode return (resets each episode; drives the per-episode curve). */
  episodeReturn = 0;
  /** Return of the LAST COMPLETED episode — what the curve plots. */
  lastEpisodeReturn = 0;
  /** Total gold eaten across all episodes this run (cumulative). */
  goldEaten = 0;
  /** Gold eaten in the CURRENT episode (resets each episode). */
  episodeGoldEaten = 0;
  /** Gold eaten in the LAST COMPLETED episode (for the curve + HUD). */
  lastEpisodeGold = 0;
  /** Total bumps across all episodes this run. */
  bumps = 0;
  /** Episode counter for this run. */
  episodes = 0;
  /** Per-round counters (reset at the start of each fit round). */
  roundReturn = 0;
  roundGold = 0;
  roundEpisodes = 0;

  /** Read-only view of total steps across all episodes (for the HUD). */
  get totalStepsRead(): number {
    return this.totalSteps;
  }

  private rng: () => number;
  /** Reusable feature buffers (avoid per-tick allocation). */
  private featBuf: Float64Array;
  private nextFeatBuf: Float64Array;
  private qBuf: Float64Array;
  private maskBuf: Uint8Array;
  /** Reusable bulk-Q scratch buffer for fitRound. Sized for the worst case
   *  (whole replay buffer × N_ACTIONS). */
  private bulkBuf: Float64Array;
  private bulkSize = 0;

  constructor(opts: AgentOptions = {}) {
    const full = { ...DEFAULT_AGENT_OPTIONS, ...opts };
    this.rng = makeRng(full.seed);
    this.q = new LinearQ({ lambda: opts.lambda, gamma: opts.gamma });
    this.replay = new Replay(full.replayCapacity);
    this.featBuf = new Float64Array(N_ACTIONS * N_FEATURES);
    this.nextFeatBuf = new Float64Array(N_ACTIONS * N_FEATURES);
    this.qBuf = new Float64Array(N_ACTIONS);
    this.maskBuf = new Uint8Array(N_ACTIONS);
    // bulkBuf grows on demand in maybeFitRound().
    this.bulkBuf = new Float64Array(0);
  }

  /** Start a fresh run. Resets all counters and the replay buffer; the Q
   *  weights are NOT reset (startRun is called once per user-press of Start;
   *  episodes within the run use startEpisode). */
  startRun(): void {
    this.roundCount = 0;
    this.epsilon = epsilonForRound(0);
    this.totalSteps = 0;
    this.returnG = 0;
    this.episodeReturn = 0;
    this.lastEpisodeReturn = 0;
    this.goldEaten = 0;
    this.episodeGoldEaten = 0;
    this.lastEpisodeGold = 0;
    this.bumps = 0;
    this.episodes = 0;
    this.roundReturn = 0;
    this.roundGold = 0;
    this.roundEpisodes = 0;
    this.replay.clear();
    this.lastFit = null;
    // Q weights stay loaded across runs (the user might be tweaking γ/λ and
    // re-running). Call resetWeights() explicitly to wipe.
  }

  /** Start the next episode within a run. Snapshots the just-finished
   *  episode's stats into `lastEpisode*` (for the curve), bumps the episode
   *  counter, and resets the per-episode counters. */
  startEpisode(): void {
    this.lastEpisodeReturn = this.episodeReturn;
    this.lastEpisodeGold = this.episodeGoldEaten;
    this.episodeReturn = 0;
    this.episodeGoldEaten = 0;
    this.episodes++;
    this.roundEpisodes++;
  }

  /**
   * One tick. Computes candidate features from the env's belief, picks a
   * masked ε-greedy action, steps the env, and appends the transition to
   * the replay buffer. Returns a StepResult for the HUD.
   */
  act(env: Env): StepResult {
    const X = candidateFeatures(env, this.featBuf);
    this.q.predictInto(X, this.qBuf);
    // Build the pathClear mask.
    const mask = this.maskBuf;
    for (let a = 0; a < N_ACTIONS; a++) {
      mask[a] = X[a * N_FEATURES + PATH_CLEAR_FEAT] >= 0.5 ? 1 : 0;
    }
    const action = selectAction(this.qBuf, mask, this.epsilon, this.rng);
    // Snapshot the taken action's features BEFORE stepping (the env will
    // mutate the belief, so we must read φ(s, a) now).
    const xTaken = Float64Array.from(X.subarray(action * N_FEATURES, (action + 1) * N_FEATURES));

    const out = env.step(action);
    this.totalSteps++;
    this.returnG += out.reward;
    this.episodeReturn += out.reward;
    this.roundReturn += out.reward;
    if (out.event === 'gold') {
      this.goldEaten++;
      this.episodeGoldEaten++;
      this.roundGold++;
    }
    if (out.event === 'bump') this.bumps++;

    // Compute φ(s') for the resulting state. Even if done, we store the
    // final-state matrix — fqi.ts gates the bootstrap by the done flag.
    candidateFeatures(env, this.nextFeatBuf);
    this.replay.add(xTaken, out.reward, this.nextFeatBuf, out.done);

    return {
      action,
      reward: out.reward,
      done: out.done,
      event: out.event,
      qValues: this.qBuf,
      epsilon: this.epsilon,
    };
  }

  /**
   * Refit Q on the accumulated replay buffer — one step of Fitted Q-Iteration.
   * Called by main.ts at each episode boundary (or every few episodes). The
   * round counter advances, ε tightens, and the next round's collection
   * proceeds on the improved Q.
   *
   * Returns the round stats for the HUD; null if the buffer is empty.
   */
  fitRound(): RoundStats | null {
    // Snapshot this round's collection stats before they reset.
    const roundReturn = this.roundReturn;
    const roundGold = this.roundGold;
    const roundEpisodes = this.roundEpisodes;
    // Ensure the bulk scratch buffer is big enough.
    const needed = this.replay.size * N_ACTIONS;
    if (this.bulkSize < needed) {
      this.bulkBuf = new Float64Array(needed);
      this.bulkSize = needed;
    }
    const res = fitRound(this.q, this.replay, this.bulkBuf);
    if (res.bufferSize === 0) return null;
    this.roundCount++;
    this.epsilon = epsilonForRound(this.roundCount);
    this.roundReturn = 0;
    this.roundGold = 0;
    this.roundEpisodes = 0;
    const stats: RoundStats = {
      ...res,
      roundCount: this.roundCount,
      epsilonUsed: this.epsilon,
      roundReturn,
      roundGold,
      roundEpisodes,
    };
    this.lastFit = stats;
    return stats;
  }

  /** Reset Q weights to zero (untrained). The "fresh start" hook. */
  resetWeights(): void {
    this.q.reset();
    this.roundCount = 0;
    this.epsilon = epsilonForRound(0);
  }

  /** Export Q weights as a plain number[] (for save/JSON). Length N_FEATURES+1. */
  exportWeights(): number[] {
    return this.q.exportWeights();
  }

  /** Load Q weights from a number[] of length N_FEATURES+1. */
  loadWeights(w: ArrayLike<number>): void {
    this.q.loadWeights(w);
  }
}
